// Tiny module-cached hook over `/api/primers`.
//
// Centralises the primer index so every surface (Navbar hint, University
// Admission tab, Apply landing count badges) reads the same live number
// instead of a hardcoded "8-country" string. The response is small and
// static per deploy, so we cache it in module scope and only fire one
// request per page load, no matter how many components mount.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

let _cache = null;
let _pending = null;

const _EMPTY = { count: null, codes: [], byCode: {}, loaded: false };

export function usePrimers() {
  const [state, setState] = useState(_cache || _EMPTY);

  useEffect(() => {
    if (_cache) {
      setState(_cache);
      return;
    }
    if (!_pending) {
      _pending = api
        .get("/primers")
        .then((res) => {
          const results = res.data?.results || [];
          _cache = {
            count: res.data?.count ?? results.length,
            codes: results.map((r) => (r.code || "").toLowerCase()),
            byCode: Object.fromEntries(
              results.map((r) => [(r.code || "").toLowerCase(), r])
            ),
            loaded: true,
          };
          return _cache;
        })
        .catch(() => {
          // Fall through to the "no primers" shape so UI collapses the
          // link gracefully instead of blowing up the whole menu.
          _cache = { ..._EMPTY, loaded: true };
          return _cache;
        });
    }
    let cancelled = false;
    _pending.then((val) => {
      if (!cancelled) setState(val);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

// Pure helper so non-React modules (or memos) can derive the primer code
// from a university's `country` display (e.g. "UK" → "uk"). Returns null
// if we don't yet cover that country.
//
// University docs store `country` inconsistently: some as 2-letter ISO
// codes ("UK", "US"), others as full names ("Sweden", "Hong Kong",
// "Netherlands"). We handle both — direct lowercase match wins first,
// then we fall back to a name → code lookup covering every country we
// currently have primers for (plus a few common aliases).
const _NAME_TO_CODE = {
  "united kingdom": "uk", "great britain": "uk", "england": "uk",
  "scotland": "uk", "wales": "uk", "northern ireland": "uk",
  "united states": "us", "united states of america": "us", "usa": "us",
  "united arab emirates": "ae", "uae": "ae",
  "sweden": "se",
  "hong kong": "hk", "hong kong sar": "hk",
  "ireland": "ie", "republic of ireland": "ie", "eire": "ie",
  "netherlands": "nl", "the netherlands": "nl", "holland": "nl",
  "germany": "de", "deutschland": "de",
  "france": "fr",
  "italy": "it", "italia": "it",
  "spain": "es", "españa": "es",
  "portugal": "pt",
  "switzerland": "ch", "suisse": "ch", "schweiz": "ch",
  "australia": "au",
  "canada": "ca",
  "new zealand": "nz",
  "singapore": "sg",
  "japan": "jp", "nippon": "jp",
  "korea": "kr", "south korea": "kr", "republic of korea": "kr",
  "china": "cn",
  "india": "in",
  "denmark": "dk",
  "norway": "no",
  "finland": "fi",
  "austria": "at", "österreich": "at",
  "belgium": "be",
  "czech republic": "cz", "czechia": "cz",
  "poland": "pl",
  "greece": "gr",
  "south africa": "za",
  "saudi arabia": "sa", "ksa": "sa",
};

export function primerCodeForCountry(country, codes) {
  if (!country) return null;
  const k = String(country).toLowerCase().trim();
  if (codes && codes.includes(k)) return k;
  const mapped = _NAME_TO_CODE[k];
  return mapped && codes && codes.includes(mapped) ? mapped : null;
}
